import React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useAuthenticationState } from '@/hooks/use-authentication';
import { FeatureLocked } from '@/components/feature-locked';
import { FullScreenLoader } from '@/components/full-screen-loader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { HeroKpi } from '@/components/hero-kpi';
import { InsightsPanel } from '@/components/insights-panel';
import { computeABComparisonInsight } from '@/helpers/analytics-insights.helper';
import { exportCampaignEntryPoints, exportCampaignTrackingLinks, exportTrackingLinkEntryPoints, fetchABTesting, } from '@/services/ab-testing';
import { downloadBlob } from '@/lib/download';
import { isAtLeastSolo, isTeam } from '@/lib/role';
import { ResultsCard } from '@/routes/console/_authenticated/_layout/campaigns/$publicId/_layout/ab-testing/_layout/-features/result-card';
export const Route = createFileRoute('/console/_authenticated/_layout/campaigns/$publicId/_layout/ab-testing/_layout/')({
    component: OverviewPage,
});
function OverviewPage() {
    const { publicId } = Route.useParams();
    const { workspace } = useAuthenticationState();
    const { from, to, excludeDevices, excludeBrowsers } = Route.useSearch();
    const canUseAbTesting = workspace && isAtLeastSolo(workspace);
    const isTeamPlan = workspace && isTeam(workspace);
    const [fromDate, toDate] = React.useMemo(() => [new Date(from), new Date(to)], [from, to]);
    const { data, isPending, isError } = useQuery({
        queryKey: ['ab-testing', 'overview', publicId, fromDate, toDate, excludeDevices, excludeBrowsers],
        queryFn: () => fetchABTesting({
            publicId,
            from: fromDate,
            to: toDate,
            excludeDevices,
            excludeBrowsers,
        }),
        enabled: canUseAbTesting,
    });
    if (!canUseAbTesting) {
        return (<FeatureLocked title="A/B Testing – Overview" description="See what’s winning across destinations and placements." plan="Solo+" features={['Destinations comparison', 'Smart Links comparison', 'Smart Links per Destination']}/>);
    }
    if (isPending)
        return <FullScreenLoader text="Loading A/B Testing overview..."/>;
    if (isError || !data) {
        return (<div className="w-full h-full flex items-center justify-center">
                <p className="text-center text-sm text-muted-foreground">Failed to load A/B Testing overview.</p>
            </div>);
    }
    // Overview should not be overwhelming
    const trackingLinks = limitComparison(data.trackingLinks, 5);
    const entryPoints = limitComparison(data.entryPoints, 5);
    // ✅ new: array preview, not map
    const trackingLinksEntryPointsPreview = (data.trackingLinksEntryPoints ?? []).slice(0, 3);
    const totalScans = (data.trackingLinks?.totalScans ?? 0) + (data.entryPoints?.totalScans ?? 0);
    const insights = collectABInsights(data);
    return (<div className="flex flex-col gap-6">
            {/* HERO + INSIGHTS */}
            <div className="grid gap-4 lg:grid-cols-3">
                <HeroKpi className="lg:col-span-2" label="A/B testing volume" value={totalScans.toLocaleString()} sublabel={totalScans > 0 ? 'scans across destinations + smart links in the selected window' : undefined}/>
                <InsightsPanel insights={insights} empty="Run a few scans and we'll surface what's winning."/>
            </div>

            {/* 1) Destinations */}
            <ResultsCard title="Destinations" description="Which destination links perform best across the whole campaign." comparison={trackingLinks} itemName="Destination" actions={<div className="flex items-center gap-2">
                        <Button asChild size="sm" variant="outline">
                            <Link to="/console/campaigns/$publicId/ab-testing/destinations" params={{ publicId }}>
                                View
                            </Link>
                        </Button>

                        {isTeamPlan ? (<Button size="sm" variant="outline" onClick={async () => {
                    const res = await exportCampaignTrackingLinks({
                        publicId,
                        from: new Date(from),
                        to: new Date(to),
                        excludeDevices,
                        excludeBrowsers,
                        fileFormat: 'csv',
                    });
                    downloadBlob(res.data, `adora-ab-tracking-links-${publicId}.csv`);
                }}>
                                Export CSV
                            </Button>) : null}
                    </div>}/>

            {/* 2) Campaign Smart Links */}
            <ResultsCard title="Smart Links (Campaign)" description="Which Smart Links perform best across the whole campaign." comparison={entryPoints} itemName="Smart Link" actions={<div className="flex items-center gap-2">
                        <Button asChild size="sm" variant="outline">
                            <Link to="/console/campaigns/$publicId/ab-testing/smart-links" params={{ publicId }}>
                                View
                            </Link>
                        </Button>

                        {isTeamPlan ? (<Button size="sm" variant="outline" onClick={async () => {
                    const res = await exportCampaignEntryPoints({
                        publicId,
                        from: fromDate,
                        to: toDate,
                        excludeDevices,
                        excludeBrowsers,
                        fileFormat: 'csv',
                    });
                    downloadBlob(res.data, `adora-ab-smart-links-${publicId}.csv`);
                }}>
                                Export CSV
                            </Button>) : null}
                    </div>}/>

            {/* 3) Smart Links per Destination (preview) */}
            <Card>
                <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-4">
                        <div className="space-y-1">
                            <CardTitle className="text-base">Smart Links (By Destination)</CardTitle>
                            <p className="text-sm text-muted-foreground">
                                Each tracking link has its own placement comparison. This avoids false comparisons.
                            </p>
                        </div>

                        <Button asChild size="sm" variant="outline">
                            <Link to="/console/campaigns/$publicId/ab-testing/smart-links-by-destination" params={{ publicId }}>
                                View all
                            </Link>
                        </Button>
                    </div>
                </CardHeader>

                <CardContent className="space-y-6">
                    {trackingLinksEntryPointsPreview.length === 0 ? (<div className="rounded-md border border-dashed p-4 bg-muted/20">
                            <p className="text-sm text-muted-foreground">No data yet.</p>
                        </div>) : (trackingLinksEntryPointsPreview.map((item) => (<TrackingLinkEntryPointsPreview key={item.trackingLinkPublicId} trackingLink={item} from={fromDate} to={toDate} excludeDevices={excludeDevices} excludeBrowsers={excludeBrowsers} isTeamPlan={isTeamPlan}/>)))}
                </CardContent>
            </Card>
        </div>);
}
/**
 * keep the overview compact
 */
function limitComparison(comparison, limit) {
    return {
        ...comparison,
        results: comparison.results.slice(0, limit),
    };
}
const TrackingLinkEntryPointsPreview = ({ trackingLink, from, to, excludeDevices, excludeBrowsers, isTeamPlan, }) => {
    const { trackingLinkPublicId, trackingLinkLabel, totalScans, results } = trackingLink;
    return (<div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
                <div className="space-y-0.5">
                    <div className="text-sm font-medium">{trackingLinkLabel}</div>
                    <div className="text-xs text-muted-foreground">TrackingLink ID: {trackingLinkPublicId}</div>
                </div>

                <div className="flex items-center gap-2">
                    <Badge variant="secondary">{totalScans} scans</Badge>

                    {isTeamPlan ? (<Button size="sm" variant="outline" onClick={async () => {
                const res = await exportTrackingLinkEntryPoints({
                    publicId: trackingLinkPublicId,
                    from: new Date(from),
                    to: new Date(to),
                    excludeDevices,
                    excludeBrowsers,
                    fileFormat: 'csv',
                });
                downloadBlob(res.data, `adora-ab-smart-links-${trackingLinkPublicId}.csv`);
            }}>
                            Export CSV
                        </Button>) : null}
                </div>
            </div>

            {totalScans === 0 || results.length === 0 ? (<div className="rounded-md border border-dashed p-4 bg-muted/20">
                    <p className="text-sm text-muted-foreground">No data yet.</p>
                </div>) : (<div className="rounded-md border overflow-hidden">
                    {/* ✅ mobile-safe scroll */}
                    <div className="overflow-x-auto">
                        <table className="min-w-140 w-full text-sm">
                            <thead className="bg-muted/40">
                                <tr>
                                    <th className="px-3 py-2 text-left font-medium w-12">#</th>
                                    <th className="px-3 py-2 text-left font-medium">Smart Link</th>
                                    <th className="px-3 py-2 text-right font-medium w-24">Scans</th>
                                    <th className="px-3 py-2 text-right font-medium w-28">Share</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((r, idx) => (<tr key={`${r.publicId}-${idx}`} className="border-t">
                                        <td className="px-3 py-2 text-muted-foreground">{idx + 1}</td>
                                        <td className="px-3 py-2">
                                            <div className="font-medium truncate">
                                                {r.label} · {r.publicId}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                {r.uniqueVisitors} unique visitors
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">{r.scans}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{r.share}%</td>
                                    </tr>))}
                            </tbody>
                        </table>
                    </div>
                </div>)}
        </div>);
};
TrackingLinkEntryPointsPreview.displayName = 'TrackingLinkEntryPointsPreview';
function collectABInsights(data) {
    const out = [];
    const tl = data.trackingLinks;
    if (tl.type !== 'combination') {
        const dest = computeABComparisonInsight({
            itemName: 'Destination',
            totalScans: tl.totalScans,
            winnerLabel: tl.winner?.label,
            winnerShare: tl.winner?.share,
            runnerUpShare: tl.results[1]?.share,
        });
        if (dest)
            out.push({ ...dest, key: `destinations-${dest.key}` });
    }
    const ep = data.entryPoints;
    if (ep.type !== 'combination') {
        const sl = computeABComparisonInsight({
            itemName: 'Smart Link',
            totalScans: ep.totalScans,
            winnerLabel: ep.winner?.label,
            winnerShare: ep.winner?.share,
            runnerUpShare: ep.results[1]?.share,
        });
        if (sl)
            out.push({ ...sl, key: `smart-links-${sl.key}` });
    }
    return out;
}
